import { create } from 'zustand'
import { subscribeWithSelector } from 'zustand/middleware'

export type NetworkStatus = 'ONLINE' | 'UNSTABLE' | 'OFFLINE' | 'REALTIME'

interface ConnectionStatusState {
  // Internal state tracking
  _isOnline: boolean
  _recentRequestSuccessStatus: boolean[]
  _realtimeConnected: boolean
  _realtimeEnabled: boolean
  _lastOrderPollSuccess: boolean

  // Derived state exposed to consumers
  networkStatus: NetworkStatus
  lastOrderPollSuccess: boolean

  // Actions
  addRequestResult: (wasSuccessful: boolean) => void
  setRealtimeConnected: (isConnected: boolean) => void
  setRealtimeEnabled: (enabled: boolean) => void
  _setIsOnline: (isOnline: boolean) => void // Internal action for browser events
  _updateNetworkStatus: () => void // Internal helper to recalculate status
}

export const useConnectionStatusStore = create<ConnectionStatusState>()(
  subscribeWithSelector( // Allows subscribing to specific state changes
    (set, get) => ({
      // --- Initial State ---
      _isOnline: typeof navigator !== 'undefined' ? navigator.onLine : true,
      _recentRequestSuccessStatus: [],
      _realtimeConnected: false,
      _realtimeEnabled: false,
      _lastOrderPollSuccess: true, // Start assuming success

      networkStatus: typeof navigator !== 'undefined' && navigator.onLine
        ? 'ONLINE'
        : 'OFFLINE',
      lastOrderPollSuccess: true, // Match initial _lastOrderPollSuccess

      // --- Actions ---
      addRequestResult: (wasSuccessful) => {
        set((state) => {
          const newStatus = [wasSuccessful, ...state._recentRequestSuccessStatus].slice(0, 3)
          return {
            _recentRequestSuccessStatus: newStatus,
            _lastOrderPollSuccess: wasSuccessful,
            lastOrderPollSuccess: wasSuccessful, // Update exposed state too
          }
        })
        get()._updateNetworkStatus() // Recalculate network status
      },

      setRealtimeConnected: (isConnected) => {
        set({ _realtimeConnected: isConnected })
        get()._updateNetworkStatus()
      },

      setRealtimeEnabled: (enabled) => {
        set({ _realtimeEnabled: enabled })
        get()._updateNetworkStatus()
      },

      _setIsOnline: (isOnline) => {
        set({ _isOnline: isOnline })
        get()._updateNetworkStatus()
      },

      _updateNetworkStatus: () => {
        const { _isOnline, _realtimeConnected, _recentRequestSuccessStatus } = get()
        let newStatus: NetworkStatus

        if (!_isOnline) {
          newStatus = 'OFFLINE'
        } else if (_realtimeConnected) {
          newStatus = 'REALTIME'
        } else {
          const isStable = _recentRequestSuccessStatus.reduce(
            (acc, curr) => acc && curr,
            true,
          )
          if (isStable) {
            newStatus = 'ONLINE'
          } else {
            newStatus = 'UNSTABLE'
          }
        }
        set({ networkStatus: newStatus })
      },
    }),
  ),
)

// --- Effect for Browser Online/Offline Events ---
// This runs once when the module is imported.
if (typeof window !== 'undefined') {
  const setOnline = () => useConnectionStatusStore.getState()._setIsOnline(true)
  const setOffline = () => useConnectionStatusStore.getState()._setIsOnline(false)

  window.addEventListener('online', setOnline)
  window.addEventListener('offline', setOffline)

  // TODO: Consider adding cleanup logic if your app unmounts/remounts Stores,
  // though usually stores persist for the app lifetime.
}
